/**
 * Utility for handling chunk loading errors
 * This helps with mobile chunk loading issues
 */

import * as React from 'react';

interface ChunkErrorHandlerOptions {
  maxRetries?: number;
  retryDelay?: number;
  clearCache?: boolean;
}

export class ChunkErrorHandler {
  private static instance: ChunkErrorHandler;
  private retryCount = 0;
  private maxRetries: number;
  private retryDelay: number;
  private clearCache: boolean;

  constructor(options: ChunkErrorHandlerOptions = {}) {
    this.maxRetries = options.maxRetries || 3;
    this.retryDelay = options.retryDelay || 2000;
    this.clearCache = options.clearCache !== false;
  }

  static getInstance(options?: ChunkErrorHandlerOptions): ChunkErrorHandler {
    if (!ChunkErrorHandler.instance) {
      ChunkErrorHandler.instance = new ChunkErrorHandler(options);
    }
    return ChunkErrorHandler.instance;
  }

  isChunkError(error: Error): boolean {
    return error.message.includes('Loading chunk') ||
           error.message.includes('ChunkLoadError') ||
           error.message.includes('Failed to fetch');
  }

  async clearChunkCache(): Promise<void> {
    if (!this.clearCache || !('caches' in window)) {
      return;
    }

    try {
      const cacheNames = await caches.keys();
      const chunkCaches = cacheNames.filter(name =>
        name.includes('chunk') ||
        name.includes('static') ||
        name.includes('js')
      );

      await Promise.all(
        chunkCaches.map(cacheName => caches.delete(cacheName))
      );

      console.log('Cleared chunk caches:', chunkCaches);
    } catch (error) {
      console.warn('Failed to clear chunk cache:', error);
    }
  }

  async handleChunkError(error: Error): Promise<boolean> {
    if (!this.isChunkError(error)) {
      return false;
    }

    console.warn(`Chunk loading error (attempt ${this.retryCount + 1}/${this.maxRetries}):`, error);

    if (this.retryCount >= this.maxRetries) {
      console.error('Max retries reached for chunk loading error');
      return false;
    }

    this.retryCount++;

    // Clear cache
    await this.clearChunkCache();

    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, this.retryDelay * this.retryCount));

    // Reload the page
    window.location.reload();
    return true;
  }

  reset(): void {
    this.retryCount = 0;
  }
}

// Global error handlers
export const setupGlobalChunkErrorHandlers = (): void => {
  const handler = ChunkErrorHandler.getInstance();

  // Handle uncaught errors
  window.addEventListener('error', async (event) => {
    if (event.error) {
      await handler.handleChunkError(event.error);
    }
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', async (event) => {
    if (event.reason) {
      const handled = await handler.handleChunkError(event.reason);
      if (handled) {
        event.preventDefault();
      }
    }
  });
};

// React hook for chunk error handling
export const useChunkErrorHandler = (options?: ChunkErrorHandlerOptions) => {
  const [hasError, setHasError] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const handler = React.useMemo(() => ChunkErrorHandler.getInstance(options), [options]);

  React.useEffect(() => {
    const handleError = async (event: ErrorEvent) => {
      if (event.error && handler.isChunkError(event.error)) {
        setHasError(true);
        const handled = await handler.handleChunkError(event.error);
        if (handled) {
          setRetryCount(prev => prev + 1);
          setHasError(false);
        }
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, [handler]);

  return { hasError, retryCount, reset: () => handler.reset() };
};
